import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import { runsOnDevice } from "../api/client.js";
import { useProfiles } from "../settings/profileStore.js";
import { startScreenFor, useStartScreen } from "../settings/startScreenStore.js";

/**
 * TV-style start (owner, 2026-10-12): a profile that chose "Start on: Live TV" opens on the
 * guide, which tunes its last channel and takes it full screen (LivePage reads the marker).
 *
 * It PUSHES /live, so the history is Home (index 0) then the guide (index 1): Back from the
 * guide is the router's ordinary Back to Home, and nothing here handles Back.
 *
 * Once per cold start: `ran` lives at module scope, so it resets only with a page load. A warm
 * return, a profile switch and the website (no device, no autoplay with sound) never jump.
 */
let ran = false;

/** Test-only: lets each test begin as a fresh page load. */
export function resetTvStartForTests(): void {
  ran = false;
}

/** The marker the jump leaves for the guide, in the router's location state. */
export interface TvStartState {
  tvStart: true;
}

/**
 * True when this cold start will jump to the guide: the one place the rule lives, read by the shell
 * (App.tsx: Home is not mounted for a start that is about to leave it, which was seen on the box
 * to hold Home on screen for seconds and delay the jump) and by TvStart itself, so the two can
 * never disagree.
 */
export function tvStartPending(pathname: string = window.location.pathname): boolean {
  if (ran || !runsOnDevice() || pathname !== "/") return false;
  const { activeProfileId, profiles } = useProfiles.getState();
  if (!profiles.some((p) => p.id === activeProfileId)) return false;
  return startScreenFor(activeProfileId, useStartScreen.getState().byProfile) === "tv";
}

export function TvStart() {
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    const jump = tvStartPending(location.pathname);
    ran = true;
    if (!jump) return;
    const state: TvStartState = { tvStart: true };
    navigate("/live", { state });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}
